import { memo, useContext } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import ProductCardFromPrice from "../ProductCard/ProductCardFromPrice";
import ProductRating from "app/pages/FicheProduit/components/ProductRating/ProductRating";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RATING_TYPE } from "app/constants";
import { BadgeItem } from "app/components/ui/badge/BadgeItem";
import { CompactHotelCard } from "app/features/product/components/CompactHotelCard/CompactHotelCard";
import "./LookAlikeProduct.scss";

const LookAlikeProduct = ({
	onClick = () => {},
	uri = "",
	image = { src: "", thumbnail: "" },
	title = "",
	subtitle = "",
	tripAdvisor = { id: "", rating: 0, reviewsCount: 0, awardLabel: "" },
	startingPrice = 0,
	price = 0,
	isRental,
	offerPriceType = "",
	nbOfNights = 0,
	includeFlightOption = false,
	includeTrainOption = false,
	showTransportModeAndNightCount = true,
	isFavorite = false,
}) => {
	const { resolution } = useContext(AppGlobalsContext);
	const hotelName = subtitle || title;
	const shouldOpenInNewTab = resolution === RESOLUTION.LARGE || resolution === RESOLUTION.XLARGE;

	return (
		<CompactHotelCard
			to={{ pathname: `/${uri}` }}
			target={shouldOpenInNewTab ? "_blank" : undefined}
			onClick={onClick}
			title={title}
			subtitle={subtitle}
			photo={image.src ? { url: image.src, thumbnail: image.thumbnail } : undefined}
			badges={
				isFavorite && (
					<BadgeItem
						type="topPicks"
						data-testid="favorite-badge"
						showPrimaryText={false}
						showSecondaryText={false}
					/>
				)
			}
			rating={
				<ProductRating
					ratingType={RATING_TYPE.TRIP_ADVISOR}
					locationName={hotelName}
					hideModal
					tripAdvisor={{
						...tripAdvisor,
						hideIcon: true,
					}}
				/>
			}
			price={
				<ProductCardFromPrice
					pricePrefix={
						startingPrice > 0 ? <FormattedMessage id="general.from.label" /> : undefined
					}
					priceSuffix={
						isRental ? (
							<FormattedMessage id="general.amount.per.accommodation.suffix.label.short" />
						) : (
							<FormattedMessage id="general.amount.per.person.suffix.label.short" />
						)
					}
					travelByTrainAvailable={includeTrainOption}
					value={startingPrice || price}
					includeOfferWithFlight={includeFlightOption}
					offerPriceType={offerPriceType}
					duration={nbOfNights}
					showFromInfo={showTransportModeAndNightCount}
					suffixSize="xsmall"
				/>
			}
			showCta={Boolean(subtitle)}
			className="lookalike-product"
			data-testid="lookalike-card"
		/>
	);
};

LookAlikeProduct.propTypes = {
	onClick: PropTypes.func,
	uri: PropTypes.string,
	image: PropTypes.shape({
		src: PropTypes.string,
		thumbnail: PropTypes.string,
	}),
	title: PropTypes.string,
	subtitle: PropTypes.string,
	tripAdvisor: PropTypes.shape({
		id: PropTypes.string,
		rating: PropTypes.number,
		reviewsCount: PropTypes.number,
		awardLabel: PropTypes.string,
	}),
	startingPrice: PropTypes.number,
	price: PropTypes.number,
	offerPriceType: PropTypes.string,
	nbOfNights: PropTypes.number,
	includeFlightOption: PropTypes.bool,
	includeTrainOption: PropTypes.bool,
	isRental: PropTypes.bool,
	showTransportModeAndNightCount: PropTypes.bool,
	isFavorite: PropTypes.bool,
};

export default memo(LookAlikeProduct);
